import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Pagination, { getStoredPageSize } from '../components/Pagination';
import usePollingRefresh from '../hooks/usePollingRefresh';
import useUnsavedChangesGuard, { UNSAVED_ORDER_CHANGES_MESSAGE } from '../hooks/useUnsavedChangesGuard';
import useDeepLinkParam from '../hooks/useDeepLinkParam';
import { useHistoryRestoreGuard, useHistoryState, useOpenRowInHistory } from '../hooks/useViewInHistory';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { apiGet, apiFetch, apiThrow, API_BASE } from '../utils/api';
// 🔻 `URGENCY_PRIORITY` came off this import on 2026-08-09 with the
// `Sort: Urgency` reducer — see `components/OrderSearchAndSort.js`.
// 🔻 `ORDER_STATUSES`, `matchesCompletionFilter` and the two `showCompleted`
// storage helpers went with the status Filters popover (2026-09-30) — the
// queue TABS below replace every one of them.
import {
  MY_ORDER_TABS,
  MY_ORDER_TAB_IDS,
  myOrderTab,
  myOrderTabFromStatusParam,
} from '../constants/statuses';
import { orderMatchesSearch } from '../utils/orderSearch';
import { computeTotalPages, clampPage } from '../utils/clampPage';
import { countByTab } from '../utils/tabCounts';
import { compareOrderRows, applySortClick, orderInitialSortDir } from '../utils/tableSort';
import EmptyState from '../components/EmptyState';
import { useToastOrNoop } from '../contexts/ToastContext';
import OrderSearchAndSort from '../components/OrderSearchAndSort';
import Toolbar, { TOOLBAR_DISCLOSURE_TRIGGER } from '../components/ui/Toolbar';
import PageHeaderBand from '../components/ui/PageHeaderBand';
import Tabs from '../components/ui/Tabs';
import Dropdown from '../components/ui/Dropdown';
import { MENU_ITEM_NEUTRAL as ORDER_ACTIONS_MENU_ITEM } from '../components/OrderActionsMenu';
import OrderListPrintMenuItem from '../components/OrderListPrintMenuItem';
import { downloadOrderListExport } from '../utils/orderListExport';
import { CSV_LABEL, CSV_MENU_LABEL } from '../utils/gagCsvExport';
import useFitColumnsToScreen from '../hooks/useFitColumnsToScreen';
import useStickyToolbar from '../hooks/useStickyToolbar';
// ── THE TABLE (2026-08-11; the only layout since the cards were retired,
//    owner, 2026-10-18). Everything below is IMPORTED, not written: the same
//    shell, the same row, the same columns and the same width hook the other
//    two order pages use. **No second order table exists and none may.**
import {
  OrderTableShell,
  OrderTableRow,
  OrderMobileTableRow,
  MY_ORDERS_MOBILE_TABLE_COLUMNS,
  MY_ORDERS_MOBILE_COL_WIDTHS_KEY,
  // 🔻 `ORDER_TABLE_COLUMNS` / `ORDER_COL_WIDTHS_KEY` → the MY-ORDERS pair
  //    (2026-08-12). This page renders ELEVEN columns, not the shared twelve;
  //    see `MY_ORDERS_TABLE_COLUMNS` and `MY_ORDERS_COL_WIDTHS_KEY` for why the
  //    key MUST diverge with the column set rather than merely being allowed to.
  MY_ORDERS_TABLE_COLUMNS,
  MY_ORDERS_COL_WIDTHS_KEY,
  MAX_ORDER_COLUMN_WIDTH,
  // Task 6 (2026-08-31), Fix Round 1 Finding 1 — the mode decision lives HERE,
  // on the page, via the existing `columns` PROP. See that export's docblock
  // in `OrderTableView.js` for why this moved out of the shell.
} from '../components/OrderTableView';
import useColumnWidths from '../hooks/useColumnWidths';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
import MobileTabSearchRow, { PHONE_HEADER_TOOLBAR } from '../components/MobileTabSearchRow';
import useSwipeTabs from '../hooks/useSwipeTabs';
import { useDepartment } from '../contexts/DepartmentContext';
// 🔀 The expanded row's imports (the actions menu, the status override, the
// shared `OrderExpandedRow`, the delivery counters, Internal notes) moved with
// it to `hooks/useMyOrderExpansion.js` (2026-09-27).
import useMyOrderExpansion from '../hooks/useMyOrderExpansion';
import useHeldOpenRows from '../hooks/useHeldOpenRows';
import { PAGE_SIDE_GUTTER } from '../constants/pageLayout';
import { scrollUnderPinnedChrome } from '../hooks/useOpenAtTop';

// Icon helpers used by the empty-state branches below — kept as small
// inline components so the JSX in the render reads cleanly.
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

function MyOrders() {
  const location = useLocation();
  // 🆕 `activeDepartment` + `assignments` joined the destructure with the
  // shared inline editor (Phase 2) — `activeDepartment` scopes the cost-code /
  // payment-type fetch below (mirroring DepartmentOrders.js) AND, since
  // 2026-08-16, `fetchOrders`' own client-side display filter (its comment
  // above has the detail) and `assignments` feeds `OrderForm`'s
  // `userAssignedProductions`, exactly as DepartmentOrders.js already does.
  // Neither is an authority decision.
  // 🔀 2026-09-27: `assignments` (and the cost-code / payment-type fetch it sat
  // beside) moved with the expanded row to `hooks/useMyOrderExpansion.js`,
  // which reads its own; this page keeps `activeDepartment` for `fetchOrders`.
  const { activeDepartment, registerDirtyEditGuard } = useDepartment();
  // Shared toast (or a no-op outside the app shell — see useToastOrNoop):
  // carries the received-tick PATCH failures (the line-item
  // rows have no inline message strip of their own), and — since the
  // `?order=` deep-link below — the filter-reset / not-found notices.
  const { error: toastError, info: toastInfo } = useToastOrNoop();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  /* 📱 THE PHONE SEARCH ROW (2026-10-01, owner) — below `md` the box hides
     behind a magnifier at the LEFT of the tab row: `components/MobileTabSearchRow.js`
     (extracted from here the same day, shared with Department Orders). */

  /**
   * ── ?order= deep-link (My Hires "view order", for a viewer who lacks
   * `canViewDepartmentOrders`) ─────────────────────────────────────────
   * Same capture/strip hook `DepartmentOrders.js` uses
   * (`hooks/useDeepLinkParam.js`) — read the id, strip it from the URL
   * IMMEDIATELY so a refresh cannot re-trigger it. Everything past capture
   * is THIS page's own: see the resolve effect declared after the
   * page-reset effect below (its own docblock explains the ordering) for
   * the pagination-jump / filter-reset / give-up logic.
   *
   * 🔑 **NOT SHARED WITH `DepartmentOrders.js`'s RESOLVE EFFECT, ON
   * PURPOSE** — `hooks/useDeepLinkParam.js`'s own docblock says the resolve
   * half "stays in their own separate resolve effects, unmoved" for each
   * real caller, and this page's filter model genuinely differs: no
   * `productionFilter` (this page has none), a different default-reset
   * (its own tab strip's `All`), and a different row testid
   * (`order-row-<id>`, not `dept-order-row-<id>`). Bending this page's
   * resolve logic to share DepartmentOrders' would need flags and
   * branches for differences that are real, not incidental — the "honest
   * duplication" the owner's own caveat sanctions over a Frankenstein
   * abstraction.
   */
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const deepLinkResetDone = useRef(false);
  useDeepLinkParam('order', (id) => {
    deepLinkResetDone.current = false;
    setPendingOrderId(id);
  });
  // Expand-to-edit dirty guard — copied from `DepartmentOrders.js` (Phase 2).
  // `editDirtyId` holds the id of the row whose inline form has unsaved
  // changes; `hooks/useMyOrderExpansion.js` feeds it to
  // `hooks/useDirtyContinuation.js`, which ASKS (the editor's three-way
  // prompt) before any gesture that would lose it, then runs that gesture.
  const [editDirtyId, setEditDirtyId] = useState(null);
  /**
   * Sticky toolbar.
   *
   * 🔀 **`headerTop` IS NOW READ, AND THE COMMENT THAT SAID IT WAS NOT HAS
   * EXPIRED (2026-08-11).** It read: *"Only `toolbarRef` is used: this page has
   * no SECOND-level sticky header, so the hook's measured `headerTop` has
   * nothing to offset."* That was true while this page had no table. The table
   * layout's `<thead>` IS a second-level sticky header, and `OrderTableShell`
   * needs the MEASURED offset — never a constant, because the toolbar wraps to
   * a second row on a narrow screen and a guessed value leaves the region
   * either floating in a gap or tucked behind the bar.
   */
  const { toolbarRef, headerTop } = useStickyToolbar();

  // Mobile detection: below md breakpoint (768px). Forces page size to 50 on
  // mobile, picks the PHONE table and turns on swipe-to-tab. The order cards
  // were retired 2026-10-18 (owner): desktop is always the table.
  const isMobile = useIsMobileOrderViewport();

  /* 🔑 THE PHONE TABLE is a different COLUMN SET, so it has its own width
     model and key (`MY_ORDERS_MOBILE_COL_WIDTHS_KEY`) — both hooks are always
     called (hook order), and only the active pair reaches the shell. */
  const isPhoneTable = isMobile;





  /**
   * ── User-draggable column widths — ELEVEN COLUMNS, OWN KEY ───────────
   *
   * 🔻 **THIS BLOCK SAID THE OPPOSITE UNTIL 2026-08-12, AND THE OLD TEXT IS THE
   * REASON THE NEW TEXT IS RIGHT — IT PREDICTED THIS CHANGE AND ITS COST.** It
   * read: *"THE SAME `sfx_order_table_col_widths` THE OTHER TWO PAGES USE, AND
   * SHARING IT IS FORCED BY THE COLUMN MODEL … A SUBSET COLUMN MODEL WOULD HAVE
   * FORCED ITS OWN KEY, AND SILENTLY CORRUPTED THIS ONE IF IT HAD NOT."* The
   * owner then approved dropping `Ordered By` (*"on my orders we can drop the
   * ordered by column as you said. Just look out for unintended consequences"*),
   * so the subset arrived and the key diverged with it, exactly as written.
   *
   * 🚨 **THE KEY IS NOT A PREFERENCE — SHARING IT WOULD CORRUPT THE OTHER TWO
   * PAGES ON THE FIRST DRAG HERE.** `useColumnWidths`'s `sanitise` builds a
   * FRESH map from the CALLER's own `columns` and copies nothing else, and the
   * hook writes the whole map back on every change; an eleven-column page on the
   * shared key would write back a map with `ordered_by` gone, resetting that
   * column's stored width for Department Orders and To Approve, silently, for
   * every user. Full reasoning at `MY_ORDERS_COL_WIDTHS_KEY`.
   *
   * 🕶️ **The general rule this leaves behind: a shared storage key is only as
   * shared as the MODEL behind it. When two surfaces stop rendering the same
   * columns, the key must split in the same commit** — the sharing argument was
   * never about the component, it was about the column set.
   *
   * 🔴 The accepted cost: a width dragged on Department Orders no longer follows
   * the user here. That is what "the two surfaces genuinely differ" means.
   */
  const {
    widths: columnWidths,
    setWidth: setColumnWidth,
    resetColumn: resetColumnWidth,
  } = useColumnWidths(MY_ORDERS_TABLE_COLUMNS, MY_ORDERS_COL_WIDTHS_KEY, MAX_ORDER_COLUMN_WIDTH);
  const {
    widths: mobileColumnWidths,
    setWidth: setMobileColumnWidth,
    resetColumn: resetMobileColumnWidth,
  } = useColumnWidths(MY_ORDERS_MOBILE_TABLE_COLUMNS, MY_ORDERS_MOBILE_COL_WIDTHS_KEY, MAX_ORDER_COLUMN_WIDTH);
  const activeTableColumns = isPhoneTable ? MY_ORDERS_MOBILE_TABLE_COLUMNS : MY_ORDERS_TABLE_COLUMNS;
  const activeColumnWidths = isPhoneTable ? mobileColumnWidths : columnWidths;
  const activeSetColumnWidth = isPhoneTable ? setMobileColumnWidth : setColumnWidth;
  const activeResetColumnWidth = isPhoneTable ? resetMobileColumnWidth : resetColumnWidth;

  /**
   * `Fit columns to screen` (owner ruling 2026-09-28, answering whether this
   * page should get it too: *"add it in and a export and print options like
   * the other pages have"*). Same shape as `DepartmentOrders.js` / `Approvals.js`
   * — see either for the full reasoning; this page's own eleven-column model
   * and its OWN width key (`MY_ORDERS_TABLE_COLUMNS` / `MY_ORDERS_COL_WIDTHS_KEY`,
   * above) are what it fits against, never the shared twelve.
   */
  const [tableRegionWidth, setTableRegionWidth] = useState(null);
  const { fitColumnsToScreen, canFitColumns, alreadyFit: columnsAlreadyFit } = useFitColumnsToScreen({
    columns: activeTableColumns,
    widths: activeColumnWidths,
    setWidth: activeSetColumnWidth,
    maxWidth: MAX_ORDER_COLUMN_WIDTH,
    available: tableRegionWidth,
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  const effectivePageSize = isMobile ? 50 : pageSize;

  // Read URL query params for the initial tab
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentUser = localStorage.getItem('sfx_requested_by') || '';
  const currentUserId = localStorage.getItem('sfx_user_id') || '';

  /**
   * ── THE TAB STRIP (owner ruling 2026-09-30) ───────────────────────────
   * *"Replace the All/Actionable toggle AND the status Filters popover with
   * queue tabs using the shared ui/Tabs (as Dept Orders does) … each with a
   * count, every order in exactly ONE tab."* The mapping is
   * `constants/statuses.js#myOrderTab`; this page only counts and filters.
   *
   * 🔻 GONE WITH IT: `selectedStatuses` (+ `sfx_status_filters_my`),
   * `filterMode`, `showCompleted` (+ `sfx_my_orders_hide_completed`) and the
   * chip row that mitigated the buried popover — a tab strip is always
   * visible, so there is no hidden filter left for a chip to report. The two
   * storage keys are simply no longer read or written.
   *
   * 🔑 NOT PERSISTED, matching Department Orders' 2026-09-13 ruling (a list
   * page opens on its default, never on a stale scope from last week). The
   * default is `all`, which is what this page showed before the tabs.
   *
   * 🔗 `?status=` STILL WORKS — `MyDashboard.js` links here with a status (or
   * `outstanding`); the rule is `constants/statuses.js#myOrderTabFromStatusParam`,
   * which maps through the SAME `myOrderTab`, so a link can never land on a tab
   * that disagrees with the rows. `outstanding` spans four tabs and lands on All.
   */
  const tabFromUrl = useCallback(
    (params) => myOrderTabFromStatusParam(params.get('status')),
    [],
  );
  // 🔀 2026-10-13: a browser Back/Forward returns to the tab its history entry
  // was on (`hooks/useViewInHistory`); a fresh visit or a link still follows
  // `?status=`, else All.
  const [activeTab, setActiveTab] = useHistoryState('my-orders-tab', () => tabFromUrl(urlParams), (v) => MY_ORDER_TAB_IDS.includes(v));

  // Sorting
  const [sortField, setSortField] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');

  // Follow a later `?status=` navigation (e.g. from the dashboard while mounted).
  // A LINK only: on a Back/Forward the entry's own remembered tab is restored
  // above (and asks first over an unsaved edit), where this bare set would
  // override it and could drop the edit.
  const navigationType = useNavigationType();
  const prevSearchRef = useRef(location.search);
  useEffect(() => {
    if (prevSearchRef.current !== location.search) {
      prevSearchRef.current = location.search;
      if (navigationType !== 'POP') setActiveTab(tabFromUrl(new URLSearchParams(location.search)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, tabFromUrl]);

  // Count rejected orders (the user needs to edit/resubmit) — the header
  // band's `· N to resubmit`. Equal to the Needs action tab's count, but kept
  // on its own predicate: the band's wording is this page's own sentence.
  const actionableCount = useMemo(() => {
    return orders.filter(o => o.status === 'rejected').length;
  }, [orders]);

  /**
   * Per-tab counts, over ALL the viewer's (department-scoped) orders — before
   * search and pagination, for the reason Department Orders gives: a tab count
   * says what is waiting ELSEWHERE, so narrowing it by the search box would
   * read `Deliveries 0` for somebody whose search is about something else.
   * `all` is the ROW count, never a sum, so an unrecognised status still
   * counts in All. A `0` renders (`ui/Tabs`' `count != null`).
   */
  const tabCounts = useMemo(() => countByTab(orders, MY_ORDER_TAB_IDS, myOrderTab), [orders]);

  const myOrderTabs = useMemo(
    () => MY_ORDER_TABS.map((t) => ({ ...t, count: tabCounts[t.id] })),
    [tabCounts]
  );

  // 🔑 CLIENT-SIDE DISPLAY FILTER, NOT AN AUTHORITY CHANGE (2026-08-16).
  // Owner: *"My orders should ONLY show my orders from the department I've
  // selected that I'm in."* The viewer is already fully authorised to see
  // every order this endpoint returns (`GET /api/orders?user_id=<self>`
  // scopes on IDENTITY, not department) — this simply narrows what is
  // DISPLAYED to the active department, so it filters `data` here rather
  // than adding a `department_id` param to the server route.
  // `activeDepartment` carries `department_id`, never `id` — see
  // `contexts/DepartmentContext.js`'s pinned rule. A My-Space (personal
  // WORKSPACE) order carries the SAME `department_id` as the personal
  // department that was active when it was created (`NewOrder.js` stamps
  // `activeDepartment.department_id` unconditionally), so this filter shows
  // a My-Space order exactly when My Space is the active context and hides
  // it otherwise — no special-casing needed there.
  //
  // 🔀 **A TRUE `department_id === null` ORDER NOW ROUTES TO MY SPACE ONLY —
  // OWNER RULING, 2026-08-17. THIS SUPERSEDES THE "NEVER FILTERED" TEXT THAT
  // USED TO STAND HERE; DO NOT RESTORE IT.** It is the rare "SOLO order" the
  // server still allows (`routes/orders.js`, a caller with no department
  // assignment at all), a legacy shape distinct from the My-Space case
  // above — a My-Space order carries the personal department's OWN
  // `department_id`, never null. Owner: department-less legacy rows belong
  // in My Space, not scattered under every real department. So a NULL-dept
  // order now shows ONLY when `activeDepartment.production_is_personal` is
  // true, and is hidden under a real department exactly like a genuine
  // foreign-department order.
  //
  // 🪤 **`production_is_personal`, NEVER `department_is_personal` — the trap
  // is in `utils/personalSpace.js`'s own docblock.** The two flags DISAGREE
  // on real rows in the dev database (10 measured), and `production_is_
  // personal` is the one keyed off `productions.is_personal`, the truth
  // `server/helpers/personalSpace.js` uses. Never a name match either —
  // department/production names are allowed to duplicate.
  //
  // 🔴 **THE ACTIONS MENU'S OWN `!detail.department_id` GATES ARE UNTOUCHED
  // — this is a DISPLAY filter, not an authority change.** Edit/Delete/
  // Cancel on a NULL-dept order still work exactly as before once the row
  // is visible; only WHERE it is visible changed.
  //
  // `orders` state IS the scoped set, so every downstream derivation (the
  // `N shown` / `N to resubmit` counts, pagination, `actionableCount`,
  // `refreshOneOrder`'s in-place merge) follows for free.
  // 🔀 `silent` param added 2026-08-27, matching `DepartmentOrders.js`'s
  // `fetchOrders(silent)` — the fallback half of `refreshOrderRow` (see
  // `handleQuickAction`'s cancel/complete/reopen branches below) fires
  // UNCONDITIONALLY after every mutating action, and before this it had no
  // way to avoid a full-page loading spinner on every ordinary success, not
  // only on the failure path it exists to guard against. `silent` only
  // suppresses the SPINNER; a failure still calls `setError` UNLESS
  // `opts.suppressErrors`, exactly as `DepartmentOrders.js`'s own docblock
  // argues a refetch the user's own action triggered should. The mount effect
  // below calls it unchanged (`silent` defaults `false`, `opts` defaults
  // `{}`), so first-load behaviour is untouched.
  //
  // 🔀 **`opts.suppressErrors` ADDED 2026-08-30 for the poll below —
  // byte-identical contract to `DepartmentOrders.js`'s `fetchOrders`: on
  // success `setError(null)` unconditionally (a silent refresh should still
  // clear a stale banner); on failure, set it only when NOT suppressing, and
  // rethrow when suppressing so `usePollingRefresh`'s own `console.debug`
  // logging sees the failure — the hook swallows what it is given, so a
  // silently-eaten error here would vanish twice over.**
  const fetchOrders = useCallback((silent = false, opts = {}) => {
    const { suppressErrors = false } = opts;
    if (!currentUser) {
      setLoading(false);
      return Promise.resolve();
    }
    if (!silent) setLoading(true);
    return apiGet(`${API_BASE}/api/orders?user_id=${currentUserId}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch orders');
        return res.json();
      })
      .then(data => {
        // No `activeDepartment` (e.g. a site admin with no active
        // department) → show everything, backward-compatible.
        //
        // 🔻 An `&& !isAllDepartments` clause on this filter went with the All
        // Productions mode on 2026-08-31. It existed because in that mode
        // `activeDepartment` was a truthy sentinel whose `department_id` was
        // the string `'all-my-departments'`, so this comparison was false for
        // every row and the page rendered ZERO orders. **The FILTER itself is
        // load-bearing and stays** — `GET /api/orders?user_id=` returns the
        // caller's own orders across EVERY department they belong to, so this
        // is what narrows the page to the department the user is standing in.
        const scoped = activeDepartment
          ? data.filter(o => (
              o.department_id
                ? o.department_id === activeDepartment.department_id
                : !!activeDepartment.production_is_personal
            ))
          : data;
        setOrders(scoped);
        setError(null);
        if (!silent) setLoading(false);
        // ADDITIVE (2026-10-28): the rows in hand, for the tick's move toast
        // (`hooks/useHeldOpenRows.js`) — state read back this turn would be the
        // PREVIOUS list. Every other caller ignores it.
        return scoped;
      })
      .catch(err => {
        if (!suppressErrors) setError(err.message);
        if (!silent) setLoading(false);
        if (suppressErrors) throw err;
      });
  }, [currentUser, currentUserId, activeDepartment?.department_id]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /**
   * 🔄 THE 30-SECOND BACKGROUND POLL — wired here 2026-08-30, REVERSING the
   * 2026-08-08 decision to leave it off this page. **The old ruling is kept
   * here, superseded, not deleted**, because it was sound reasoning at the
   * time and the next reader needs both dates to understand why the page
   * looks inconsistent with its own history.
   *
   * 🔻 **SUPERSEDED 2026-08-30.** `client/src/pages/CLAUDE.md`'s
   * `usePollingRefresh` entry recorded: *"Wired on `DepartmentOrders` and
   * `Approvals`, deliberately NOT on `MyOrders`: the first two are queues of
   * other people's activity that a buyer/approver sits on all day, whereas My
   * Orders is visited transiently to raise or resubmit your own order."*
   * Owner, 2026-08-30, reversing it explicitly: *"yes add the refresh to my
   * orders too"*.
   *
   * 🔑 **`paused` HERE IS `editDirtyId !== null`, THE SAME EXPRESSION
   * `DepartmentOrders.js` USES — NOT COPIED, DERIVED.** This page gained a
   * real `OrderInlineEditor` in Phase 2 (`OrderExpandedRow` renders it for a
   * requester's own order, same mechanism as the other two pages), so its
   * unsaved work is the SAME SHAPE as Department Orders' — an open edit form
   * — unlike `Approvals.js`, whose paused expression additionally tracks
   * `showRejectForm` because a typed reject reason is unsaved text this page
   * has no equivalent of (nobody rejects their own order). Verify this
   * against the page's OWN state before copying it elsewhere; it is not "the
   * usual one".
   *
   * 🪤 **TWO TRIGGERS, TWO POLICIES, AND THEY MUST STAY APART.** A tab
   * regaining focus reaches this SAME poll (`usePollingRefresh`'s
   * visibilitychange rule) and, while `editDirtyId !== null`, is SKIPPED
   * SILENTLY — no dialog, ever; the user simply sees slightly stale data
   * until the edit is saved or discarded. A DEPARTMENT SWITCH is a different
   * trigger entirely, handled in `ProductionDepartmentSwitcher.js`: it
   * ANNOUNCES first with a Continue/Cancel dialog (see the registration
   * effect below) and, on Continue, wins outright rather than deferring. Do
   * not unify the two — the whole reason this comment exists is that a later
   * reader will find that tempting.
   */
  usePollingRefresh(
    useCallback(() => fetchOrders(true, { suppressErrors: true }), [fetchOrders]),
    { paused: editDirtyId !== null }
  );

  // ── REGISTER THIS PAGE'S DIRTY STATE WITH THE SWITCH GUARD (2026-08-30) ──
  // Same mechanism as `DepartmentOrders.js` and `Approvals.js` — see
  // `DepartmentContext.js`'s `hasDirtyEdit` docblock for the full ruling.
  // `ProductionDepartmentSwitcher.js` reads this BEFORE calling
  // `switchDepartment`, to decide whether to raise the "you'll lose it"
  // dialog. Cleared on unmount so a route change away from this page cannot
  // leave a future switch blocked by an edit that no longer exists.
  useEffect(() => {
    registerDirtyEditGuard?.(editDirtyId !== null);
    return () => registerDirtyEditGuard?.(false);
  }, [editDirtyId, registerDirtyEditGuard]);

  // Leaving the PAGE with a dirty inline edit — sidebar, header, Back, reload
  // (R14, 2026-09-15; `hooks/useUnsavedChangesGuard.js`). Same `editDirtyId`
  // predicate as the switch guard above. ONE guard for the page, never one per
  // editor. In-page navigating items ask first via `continueWhenClean` and
  // navigate only once the edit is saved or discarded — so this never fires
  // on top of that prompt.
  const leaveGuard = useUnsavedChangesGuard({
    when: editDirtyId !== null,
    message: UNSAVED_ORDER_CHANGES_MESSAGE,
  });

  // A received change may auto-advance the order status; refresh the list so
  // the row's status badge (and, where present, its Delivery Progress) update.
  // The detail cache is refreshed by the shared received handlers before this
  // fires (see `utils/receivedItems.js`).
  // SILENT (owner 2026-10-04: "ticking a delivered check box refreshes the
  // page"): a spinner flip unmounts the list and the open row behind it, as
  // Department Orders and the Hires pages already avoid.
  // 📌 HELD (owner 2026-10-28, on a phone: *"i ticked 1 item as deliverd and
  // the order closed. guess it jusmped to the deliverd page?"* — `On order` →
  // `Deliveries`): the row being ticked stays on its tab while it is open, and
  // its move is announced once — Department Orders' rule, ONE hook for both.
  const { heldIds, holdThrough, releaseWhenAllClosed, tickBusy } = useHeldOpenRows({
    activeTab,
    unfiltered: activeTab === 'all',
    filterKey: [searchQuery],
    tabOf: myOrderTab,
    tabs: MY_ORDER_TABS,
    announce: toastInfo,
  });
  const handleItemsReceived = useCallback(
    (orderId) => holdThrough(orderId, () => fetchOrders(true)),
    [holdThrough, fetchOrders]
  );

  // ── THE EXPANDED ROW (2026-09-27) ─────────────────────────────────────────
  // State, handlers, the completion dialog and `renderExpanded(order)` moved
  // VERBATIM to `hooks/useMyOrderExpansion.js` so My Hires can expand a hire
  // into the same panel. This page keeps the LIST — `orders`, `fetchOrders`,
  // the poll, the leave guard, the deep link — and OWNS `editDirtyId`.
  // `handleItemsReceived` above is the tick cluster's list refresh.
  /**
   * "Mark as done" / "Restore" (owner ruling 2026-09-30) — the requester's
   * PERSONAL completion. `PATCH /api/orders/:id/my-done` writes only
   * `order_personal_done`; nothing on the order moves, so the only thing to
   * update here is this page's own `my_done` on the row, which moves it
   * between tabs. The row is patched from the server's ANSWER, never
   * optimistically, so a refusal leaves the tabs telling the truth.
   * One guard for both directions (`useSubmitGuard`): a double press is one
   * request.
   * 🏷️ `?done=1|0` on the URL is an ANALYTICS HINT, not input: the server reads
   * only the body. `activity_log` records the URL and never the body, so
   * without it Admin Analytics could not tell "Marked order #N as done" from
   * "Restored order #N" (`utils/endpointHumaniser.js`).
   */
  const { guard: guardPersonalDone } = useSubmitGuard();
  const handlePersonalDone = useCallback((order, done) => guardPersonalDone(async () => {
    try {
      const res = await apiFetch(`${API_BASE}/api/orders/${order.id}/my-done?done=${done ? 1 : 0}`, {
        method: 'PATCH',
        body: JSON.stringify({ done }),
      });
      if (!res.ok) await apiThrow(res);
      const body = await res.json();
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, my_done: body.my_done === true } : o)));
      toastInfo(done ? 'Moved to your Completed tab.' : 'Moved back out of your Completed tab.');
    } catch (err) {
      toastError(err);
    }
  }), [guardPersonalDone, toastInfo, toastError]);

  const {
    expandedOrders,
    collapseAllOrders,
    orderDetails,
    detailsLoading,
    continueWhenClean,
    toggleExpand,
    renderExpanded,
    dialogs: expansionDialogs,
  } = useMyOrderExpansion({
    setOrders,
    fetchOrders,
    editDirtyId,
    setEditDirtyId,
    currentUser,
    currentUserId,
    onListRefresh: handleItemsReceived,
    onTickBusyChange: tickBusy,
    onPersonalDone: handlePersonalDone,
  });
  // The held row is released when the user closes the last open row — never
  // by a list reload; see `hooks/useHeldOpenRows.js`.
  useEffect(() => releaseWhenAllClosed(expandedOrders), [expandedOrders, releaseWhenAllClosed]);
  // A Back/Forward that stays on this page (a bell link pushes `?order=` here)
  // asks before it drops an unsaved edit: see `useHistoryRestoreGuard`.
  useHistoryRestoreGuard(editDirtyId !== null, continueWhenClean);

  // A browser Back/Forward reopens the order this entry had open (2026-10-13),
  // through the `?order=` deep link's own request. Clean rows only (see
  // `useOpenRowInHistory`); with several rows open at once, none is remembered.
  useOpenRowInHistory('my-orders-open', expandedOrders.size === 1 ? [...expandedOrders][0] : null, (id) => {
    deepLinkResetDone.current = false;
    setPendingOrderId(id);
  });

  /* ONE tab handler for the strip's tap, its snap-to-centre settle, and a
     swipe on the list — so every route asks first over a dirty edit. */
  const handleTabChange = useCallback(
    (id) => continueWhenClean(() => { collapseAllOrders(); setActiveTab(id); }),
    [continueWhenClean, collapseAllOrders]
  );
  // A tap on the CURRENT tab also closes every opened order (owner 2026-10-03); a dirty edit asks first.
  const handleTabReselect = useCallback(() => continueWhenClean(collapseAllOrders), [continueWhenClean, collapseAllOrders]);

  /* The tab strip, rendered in ONE of two wrappers (phone row / desktop bar)
     so its props cannot drift between them. */
  const myOrdersTabStrip = (className) => (
    <Tabs
      bare
      className={className}
      tabs={myOrderTabs}
      active={activeTab}
      onChange={handleTabChange}
      onReselect={handleTabReselect}
      idPrefix="my-orders-tab"
      ariaLabel="My order tabs"
      scrollHints
      /* 📱 Below `md`: the strip snaps tabs to the centre and the tab that
         SETTLES there becomes active; it re-centres when a swipe on the
         list changes tab. `ui/Tabs`' own docblock has the rules.
         `snapFlushStart` (2026-10-01): the first tab rests at the LEFT edge,
         right after the search icon, instead of in the strip's centre. */
      snapCenter
      snapFlushStart
    />
  );

  /* 📱 SWIPE THE LIST TO CHANGE TAB (below `md`, 2026-09-30). The rules —
     vertical wins, jitter ignored, the table's own horizontal scroll wins
     unless it started at its edge, reduced motion switches instantly — are
     `utils/swipeTabs.js`; the wiring is `hooks/useSwipeTabs.js`. */
  const swipeAreaRef = useRef(null);
  const swipeHandlers = useSwipeTabs(swipeAreaRef, {
    tabs: myOrderTabs,
    active: activeTab,
    onChange: handleTabChange,
    enabled: isMobile,
  });













  // Filtered orders (memoized so hooks below have stable deps)
  const filteredOrders = useMemo(() => {
    const result = orders.filter(o => {
      // The tab (2026-09-30). `all` is the ABSENCE of a filter, never a
      // bucket — `myOrderTab` cannot answer it — so an order with a status
      // the mapping does not know still shows in All.
      // `heldIds` (2026-10-28) is the row being ticked — a LINGER on the tab
      // predicate only; `tabCounts` never reads it, so the counts stay true.
      if (activeTab !== 'all' && myOrderTab(o) !== activeTab && !heldIds.has(o.id)) return false;
      // Text search via shared utility (matches reference, po_number,
      // requester, supplier, dept/production, status, urgency, notes, line
      // items — personal_ref came off the list 2026-08-12, see orderSearch.js).
      return orderMatchesSearch(o, searchQuery);
    });

    // Sort — THE order-list comparator (`utils/tableSort.js`).
    result.sort((a, b) => compareOrderRows(a, b, sortField, sortDir));

    return result;
  }, [orders, searchQuery, activeTab, sortField, sortDir, heldIds]);

  /**
   * `Actions ▾` — ADDED 2026-09-28 (owner, asked whether My Orders and Budget
   * Cost Codes should get `Fit columns to screen` too: *"add it in and a
   * export and print options like the other pages have"*). Same shape as
   * `DepartmentOrders.js`'s / `Approvals.js`'s own — a `ui/Dropdown`, every
   * item's gate written beside it, table-gated `Fit columns to screen` last.
   *
   * 🔑 **EXPORT CSV IS CLIENT-SIDE HERE, NOT `GET /api/orders/export`.**
   * The other two pages' `handleExport` posts `?ids=` to that SERVER route;
   * widening it to a third caller was not part of the ruling above, and a
   * client build from rows already on screen discloses nothing new — see
   * `utils/orderListExport.js`'s own docblock. `Download list (PDF)` /
   * `Print all` reuse `OrderListPrintMenuItem` UNCHANGED (it already builds
   * client-side from a caller-supplied `orders` array with no gate).
   *
   * 🔻 **THIS SUPERSEDES the decision recorded on `PageHeaderBand` below
   * ("NO Reset column widths ITEM... this page has no such menu") — corrected
   * in the same commit, not left to go stale beside the code that disproves
   * it.**
   */
  const printFilterSummary = useMemo(() => {
    const parts = [];
    if (activeTab !== 'all') {
      const tab = MY_ORDER_TABS.find((t) => t.id === activeTab);
      parts.push(`Tab: ${tab ? tab.label : activeTab}`);
    }
    if (searchQuery) parts.push(`Search: "${searchQuery}"`);
    return parts.join(' · ');
  }, [activeTab, searchQuery]);

  const myOrdersActionsMenu = (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-64 p-1"
      triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="Page actions"
      ariaLabel="Page actions"
      triggerTestId="my-orders-actions-menu"
      label="Actions"
    >
      {({ close }) => (
        <div role="menu" aria-label="Page actions">
          {(searchQuery || activeTab !== 'all') && (
            <p className="px-3 pt-2 pb-1 text-xs text-gray-500 dark:text-gray-400">
              Exporting filtered results ({filteredOrders.length} orders)
            </p>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); downloadOrderListExport(filteredOrders, 'my-orders'); }}
            title={`Download the currently-filtered orders as ${CSV_LABEL}`}
            className={ORDER_ACTIONS_MENU_ITEM}
            data-testid="my-actions-export-csv"
          >
            {CSV_MENU_LABEL}
          </button>
          <OrderListPrintMenuItem
            orders={filteredOrders}
            headerTitle="My Orders"
            filterSummary={printFilterSummary}
            totalCount={filteredOrders.length}
            filename="my-orders.pdf"
            onDone={close}
            testId="my-actions-print-all"
            downloadTestId="my-actions-download-list"
          />
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); fitColumnsToScreen(); }}
            disabled={!canFitColumns || columnsAlreadyFit}
            title={!canFitColumns
              ? 'Nothing measured to fit to yet'
              : columnsAlreadyFit
                ? 'The columns already fit the screen'
                : 'Fit every column to the available screen width'}
            className={ORDER_ACTIONS_MENU_ITEM}
            data-testid="my-actions-fit-columns"
          >
            Fit columns to screen
          </button>
        </div>
      )}
    </Dropdown>
  );

  /**
   * A table header was clicked (the only sort control since the cards and
   * their `Sort ▾` were retired, 2026-10-18). Byte-identical to
   * `DepartmentOrders.js`'s and `Approvals.js`'s — **keep the three
   * identical, not merely similar.** Clicking the same header twice reverses it.
   *
   * `explicitDir` returns BEFORE the `desc` seeding below, which is what lets a
   * cycling column (PO, Date) declare a fixed direction per state.
   */
  // The rule itself is `utils/tableSort.js#applySortClick`, shared by all four tables.
  const handleSort = useCallback((field, explicitDir) => applySortClick(field, explicitDir, sortField, {
    setSortField, setSortDir, initialDir: orderInitialSortDir,
  }), [sortField]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeTab, sortField, sortDir]);

  /**
   * Resolve the pending `?order=` deep-link once this page's own order list
   * has loaded — the `MyOrders.js` twin of `DepartmentOrders.js`'s resolve
   * effect (see that file for the fuller account of the trap this closes;
   * this docblock states only what genuinely differs here).
   *
   * 🚨 **MUST STAY DECLARED AFTER THE PAGE-RESET EFFECT ABOVE**, for the
   * identical hook-ordering reason `DepartmentOrders.js` documents at its
   * own resolve effect: React runs effects in declaration order within one
   * render pass, and this effect's own reset branch (below) changes the
   * SAME state (`activeTab`/`searchQuery`) the page-reset effect is keyed on — so on the render
   * pass that follows a reset, both effects fire together. Declared in the
   * other order, the page-reset effect would run SECOND and silently snap
   * `currentPage` back to 1 after this effect had already jumped it
   * forward, and the row would be expanded in state but off-page.
   *
   * 🪤 THE TRAP: `GET /api/orders?user_id=` (`fetchOrders`) returns every
   * order the caller can see; `orders` state is then narrowed to the
   * active department (a display filter, unrelated to this effect); and
   * `filteredOrders` narrows further through the TAB (an off-hired hire's
   * order is almost always in Deliveries or Completed, never in the tab the
   * reader happens to be on) and search. This page also PAGINATES — a row surviving every
   * filter can still be on a page nobody is looking at.
   *
   * Not found in the current filter → reset THIS PAGE's own default filter
   * state ONCE (`deepLinkResetDone` guards the loop) — the `All` tab and an
   * empty search box (🔀 2026-09-30: was `applyOrderFilterPatch` over the
   * retired status/completion filters) — tell the user it
   * happened (a visible toast, matching Department Orders' ruling that a
   * silent reset is "mysterious"), and let `filteredOrders` recompute on
   * the next render. Still not found after the reset → give up with a
   * toast and clear the pending id so it cannot loop.
   *
   * Found → jump `currentPage` to the page holding its index in
   * `filteredOrders`, expand it via the SAME `toggleExpand` the row's own
   * chevron uses (dirty-guarded exactly as every other expand on this
   * page — an in-progress inline edit blocks it the same way), and scroll
   * it into view after paint.
   *
   * ⚠️ jsdom has no layout, scroll or paint, and does not even implement
   * `Element.prototype.scrollIntoView` — whether the row is actually
   * visible to the user afterward is an unverified BROWSER claim, not
   * something the client test suite can prove. See
   * `MyOrders.deepLinkOrder.test.js`.
   */
  useEffect(() => {
    if (pendingOrderId == null || loading || error) return;
    const idx = filteredOrders.findIndex((o) => o.id === pendingOrderId);
    if (idx === -1) {
      if (!deepLinkResetDone.current) {
        deepLinkResetDone.current = true;
        // 🪤 Only when the reset CHANGES something. On All with an empty
        // search it is a no-op: no state moves, nothing re-renders, and this
        // effect would never run again to reach the give-up below — the
        // retired status filters dodged that only because every reset built
        // fresh arrays. Nothing to widen → nothing to retry → give up now.
        if (activeTab !== 'all' || searchQuery) {
          setActiveTab('all');
          setSearchQuery('');
          toastInfo('Filters were cleared to show this order.');
          return;
        }
      }
      setPendingOrderId(null);
      toastError('That order could not be found — it may have been deleted or moved to a different department.');
      return;
    }
    const targetId = pendingOrderId;
    const size = effectivePageSize;
    const page = size === 'ALL' ? 1 : Math.floor(idx / size) + 1;
    setCurrentPage(page);
    if (!expandedOrders.has(targetId)) {
      toggleExpand(targetId);
    }
    setPendingOrderId(null);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="order-row-${targetId}"]`);
      scrollUnderPinnedChrome(el);
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOrderId, loading, error, filteredOrders, effectivePageSize]);

  // 🔑 CLAMPED (root CLAUDE.md's Pagination section, `utils/clampPage.js`): a
  // refetch that SHRINKS `filteredOrders` must never strand the view past
  // its last page. `page` is DERIVED, not written back into `currentPage`
  // state — see `clampPage.js`'s docblock for why an effect was deliberately
  // not added here. It feeds both the slice below and the Pagination
  // `currentPage` prop, so the two can never disagree; before this, this
  // page had NO top-level clamp at all — `paginatedOrders` sliced off the
  // raw `currentPage` state directly.
  const totalPages = computeTotalPages(filteredOrders.length, effectivePageSize);
  const page = clampPage(currentPage, totalPages);

  // Paginated orders — on mobile, effectivePageSize forces 50 regardless of stored preference
  const paginatedOrders = useMemo(() => {
    if (effectivePageSize === 'ALL') return filteredOrders;
    const start = (page - 1) * effectivePageSize;
    return filteredOrders.slice(start, start + effectivePageSize);
  }, [filteredOrders, page, effectivePageSize]);

  const handlePageChange = useCallback((targetPage) => {
    if (targetPage >= 1 && targetPage <= totalPages) {
      setCurrentPage(targetPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalPages]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  if (loading) {
    return (
      <div className={`max-w-full p-2 pt-0 md:p-4 md:pt-0 lg:p-6 lg:pt-0 ${PAGE_SIDE_GUTTER}`}>
        <div className="bg-white rounded-lg shadow-md p-6">
          <LoadingSpinner message="Loading orders..." />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className={`max-w-full p-2 pt-0 md:p-4 md:pt-0 lg:p-6 lg:pt-0 ${PAGE_SIDE_GUTTER}`}>
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-gray-500">Please submit an order first so we know who you are. Your name will be remembered.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`max-w-full p-2 pt-0 md:p-4 md:pt-0 lg:p-6 lg:pt-0 ${PAGE_SIDE_GUTTER}`}>
        <div className="bg-white rounded-lg shadow-md p-6">
          <ErrorMessage error={error} onRetry={fetchOrders} title="Failed to load orders" />
        </div>
      </div>
    );
  }

  const trimmedSearch = searchQuery.trim().toLowerCase();

  return (
    /**
     * 🔻 **`max-w-4xl lg:max-w-6xl xl:max-w-7xl mx-auto` → `max-w-full` (2026-08-11).**
     * Product owner, on the new table view: *"table width doesnt grow on My
     * orders"*.
     *
     * 🔑 **THIS PAGE WAS MISSED BY AN APP-WIDE RULE FOUR MONTHS AGO — the cap was
     * a LEFTOVER, not a reading-width decision.** `b276d617` (2026-04-04),
     * *"Full-width layouts for list/table pages; form pages (NewOrder, EditOrder,
     * OrderDetail) keep max-w-7xl"*, changed `DepartmentOrders.js` and
     * `Approvals.js` to `max-w-full`. It touched THIS file too — for the button
     * and indigo migrations — and left its container capped. My Orders is a list
     * page; it simply did not get the rule applied to it.
     *
     * 🚨 **THE CAP MADE THE TABLE UNABLE TO GROW AT *ANY* SCREEN WIDTH, not just
     * at wide ones, and that is why the symptom reads as "never grows" rather
     * than "stops growing".** `OrderTableShell`'s `<table>` is `w-full` with
     * `minWidth: orderTableTotalWidth(...)` (1520 by default), and it stretches
     * only when the SCROLLPORT exceeds that floor. `max-w-7xl` is 1280px, so the
     * container could never exceed 1280, the scrollport never exceeded ~1232,
     * and the table sat on its 1520 floor with a horizontal scrollbar on every
     * screen ever made.
     *
     * 📏 **MEASURED IN CHROMIUM (1920 viewport, 423 real orders), sidebar
     * COLLAPSED — the case that shows it best, because collapsing frees 192px
     * and the table gained none of it:**
     *   My Orders  BEFORE — main 1846, container **1280**, scrollport 1232,
     *                       table **1520** (floor), h-scroll YES
     *   Dept Orders        — main 1846, container **1846**, scrollport 1798,
     *                       table **1786** (stretched), h-scroll NO
     * The container did not even change when the sidebar collapsed (1280 either
     * way), which is the clearest statement of the bug.
     *
     * 🟢 **NOT A MISSING PROP, AND THAT WAS CHECKED FIRST.** `OrderTableShell`
     * measures its OWN scrollport with `useMeasuredWidth` internally and feeds
     * `OrderTableWidthContext`; the page passes no width and is not supposed to.
     * Nothing in `OrderTableView.js` needed to change, and nothing did — so
     * Department Orders and To Approve are untouched by this fix.
     *
     * ⚠️ **All FOUR container sites in this file changed together** (loading,
     * no-user, error, and this one). Changing only this one would make the page
     * visibly narrow while loading and then snap wider — a flicker introduced by
     * a width fix.
     */
    <div className={`max-w-full p-2 pt-0 md:p-4 md:pt-0 lg:p-6 lg:pt-0 ${PAGE_SIDE_GUTTER}`}>
      {leaveGuard.dialog}
      {/* ── HEADER BAND. This page had no header row at all — just a bare
          `hidden md:block` VAT badge — so it GAINS one rather than having one
          reshaped. Same shell as the other three pages.

          🚨 THE WORDING IS THIS PAGE'S OWN, AND COPYING DEPARTMENT ORDERS' WOULD
          HAVE PRINTED A FALSE STATEMENT. `actionableCount` here is
          `status === 'rejected'` (`:146-148`) — YOUR order bounced back and needs
          resubmitting — not "actionable", which on Department Orders means
          the server's role-dependent `can_action`. CLAUDE.md is explicit
          that *"Actionable means three different things and must NOT be
          unified"*; the same applies to the sentence that reports it.

          It keeps the green pair, because "needs your attention" is what both
          numbers mean even though the predicates differ — and green is what
          `constants/rowStyles.js` uses for that meaning everywhere else.

          🔀 **`actions` GAINED AN `Actions ▾` 2026-09-28 — SUPERSEDES the
          history below, corrected rather than deleted.** Owner, asked whether
          this page and Budget Cost Codes should get `Fit columns to screen`
          too: *"add it in and a export and print options like the other
          pages have"*. `components/OrderActionsMenu.js` was no longer being
          rebuilt on another branch by then, so the merge-hazard reason this
          page went without one is gone with it. `myOrdersActionsMenu` (built
          above, beside `printFilterSummary`) holds Export CSV,
          Download list (PDF), Print all and Fit columns to screen — the
          first four ungated (client-side, from rows already on screen, like
          Dept Orders' own). */}
      <PageHeaderBand
        testId="my-orders-header-band"
        hideContextOnPhone
        context={(
          <>
            My orders &middot; {orders.length} shown
            {actionableCount > 0 && (
              <span className="text-green-700 dark:text-green-400 font-medium whitespace-nowrap">
                {' '}&middot; {actionableCount} to resubmit
              </span>
            )}
          </>
        )}
        /* 🔻 NO `status` SLOT SINCE 2026-08-08 — see the note on Approvals. The
           VAT badge was its only occupant and the VAT display is gone from the
           UI; `hooks/useVatMode.js` records why restoring it is not a one-liner. */
        // 📱 Below `md` Actions ▾ rides the phone header's row 2 instead.
        actions={isMobile ? null : myOrdersActionsMenu}
      />

      {/* Toolbar — Search, then the tab strip (2026-09-30; was
          All / Actionable + Filters ▾). Same shell,
          same order and the same popover as Department Orders; the sticky
          behaviour and the z-index contract belong to `components/ui/Toolbar`.

          🚫 NO PAGE-SIZE SELECTOR HERE, and do not "restore" it. Product owner,
          2026-08-08: *"the top sticky bar is a mess, it seems to be spread over
          multiple rows etc. I would prefer as few rows as possible."* This bar
          carried a second, hand-rolled copy of the "Show 25 50 100 ALL" control
          that `components/Pagination.js:83-103` already renders at the bottom of
          this same page (`:956` below) — same four options, same
          `handlePageSizeChange`. Two details made it strictly worse than a
          duplicate: it emitted a duplicate `data-testid="page-size-<n>"`
          alongside Pagination's, and unlike Department Orders' copy it was
          visible at EVERY width while `effectivePageSize` (`:72`) forces 50
          below `md` — so on a phone it was a control that appeared to work and
          changed nothing. Pagination's own copy is `hidden md:flex`, which is
          the exact complement of this page's `(max-width: 767px)` mobile test,
          so no width loses an affordance it really had.

          🔻 NO Cards ⇄ Table TOGGLE AND NO `Sort ▾` SINCE 2026-10-18 — the
          order cards were retired (owner), so the table is the only layout
          and its column headers are the sort control. Nothing fills the
          right slot, exactly like Department Orders. */}
      <Toolbar className={PHONE_HEADER_TOOLBAR}
        ref={toolbarRef}
      >
        <OrderSearchAndSort
          searchText={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search orders by reference, supplier, production..."
          testIdPrefix="my-orders"
          // 📱 Below `md` the box lives in the collapsible search row instead
          // (the same component, the same testid — see `mobileSearchRow`).
          showSearch={!isMobile}
        />

        {/* 🔀 THE TAB STRIP (owner ruling 2026-09-30) — replaces the All /
            Actionable toggle AND the `Filters ▾` status popover, desktop and
            mobile. `ui/Tabs` in `bare` mode, exactly as Department Orders
            carries its queue tabs; the mapping is `myOrderTab`.
            📱 Below `lg` the strip takes its own full line (`w-full`); below
            `sm` it is ONE horizontally scrolling row, and `scrollHints` keeps
            the active tab in view and fades the edge with more tabs behind it
            (owner: *"one horizontally scrolling tab row, active tab scrolled
            into view, edge fade"*). ⚠️ jsdom proves neither; BROWSER checks.
            A dirty inline edit ASKS first (`continueWhenClean`), the same
            rule Department Orders' tabs follow.
            🔻 NO CHIP ROW BELOW ANY MORE: `FilterChips` existed to put a
            BURIED filter's state back on the page, and nothing is buried now —
            the strip itself shows which tab is active. */}
        {isMobile ? (
          /* 📱 The shared phone header — `components/MobileTabSearchRow.js`:
             row 1 the tabs, row 2 🔍 · Actions ▾ (off the band, 2026-10-03). */
          <MobileTabSearchRow
            idPrefix="my-orders"
            searchTestIdPrefix="my-orders"
            searchText={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search orders by reference, supplier, production..."
            tabStrip={myOrdersTabStrip('flex-1 min-w-0')}
            actions={myOrdersActionsMenu}
          />
        ) : myOrdersTabStrip('w-full lg:w-auto min-w-0')}
      </Toolbar>

      {/* 📱 THE SWIPE AREA — the list (or its empty state) under the strip.
          A plain block with no classes: at rest it is not a stacking context
          (the hook clears its transform), so it changes nothing about the
          sticky header pane or the z-index contract. Handlers are `{}` above
          `md`. */}
      <div ref={swipeAreaRef} {...swipeHandlers} data-testid="my-orders-swipe-area">
      {/* Group orders by date for mobile date separators */}
      {paginatedOrders.length === 0 ? (
        trimmedSearch ? (
          <EmptyState
            size="sm"
            icon={<SearchIcon />}
            title="No results found"
            description={`No orders matching "${searchQuery.trim()}". Try a different search term.`}
          />
        ) : activeTab !== 'all' ? (
          <EmptyState
            size="sm"
            icon={<FilterIcon />}
            title="Nothing in this tab"
            description="None of your orders are in this tab right now."
            testId="my-orders-tab-empty"
          />
        ) : (
          /* 🕶️ THIS LOOKS LIKE THE `!error` DEFECT THE REST OF THIS COMMIT
              CLOSES AND IT IS NOT — DO NOT "FIX" IT, 2026-08-22. It carries no
              `!error` term and it does not need one: `if (error) { return … }`
              at the top of the render (search `Failed to load orders`) takes
              the whole page, so this branch is UNREACHABLE while `error` is
              set. Latent, not live. Pinned here rather than repaired because
              adding a redundant `&& !error` would suggest the guard above is
              not trusted, and the next reader would go looking for the case it
              covers. 🪤 If that early return is ever softened into an inline
              banner — which is what the other two order pages do — this becomes
              a live "no orders yet, create your first one" over a failed
              request, and the term must be added in the SAME change. */
          <EmptyState
            title="No orders yet"
            description="You haven't submitted any orders yet. Get started by creating your first order."
            cta={{ label: 'Create your first order', to: '/orders/new' }}
          />
        )
      ) : (
        /* The order cards were retired 2026-10-18 (owner): desktop is always
           this table, a phone always the phone table. */
        <OrderTableShell
          testIdPrefix="my"
          /* ELEVEN columns — the shared twelve minus `Ordered By`, which on
             this page is always the viewer.
             🔑 The shell hands this to the rows through `OrderColumnsContext`, so
             the `<colgroup>`, the headings and the body cells cannot disagree —
             no page passes a column model to a row.
             🔻 Back to the bare `MY_ORDERS_TABLE_COLUMNS` constant on
             2026-08-31: the All-mode Department column it was wrapped in
             (`withAllModeDepartmentColumn`) went with the mode, along with
             the `departmentAssignments` prop that supplied that column's
             name lookup. */
          columns={activeTableColumns}
          sortField={sortField}
          sortDir={sortDir}
          onHeaderSort={handleSort}
          stickyTop={headerTop}
          columnWidths={activeColumnWidths}
          onColumnResize={activeSetColumnWidth}
          onColumnResetWidth={activeResetColumnWidth}
          onRegionWidthChange={setTableRegionWidth}
        >
          {paginatedOrders.map((order) => {
            const isExpanded = expandedOrders.has(order.id);
            const detail = orderDetails[order.id];
            const isLoadingDetail = detailsLoading[order.id];

            // 🔀 THE EXPANDED ROW MOVED, VERBATIM, TO `hooks/useMyOrderExpansion.js`
            // (2026-09-27) so My Hires can expand a hire into the same panel.
            // Every gate, handler and testid inside it is unchanged; read the
            // hook's `renderExpanded` for the per-row block that stood here.
            const expandedContent = renderExpanded(order);

            /* 📱 The PHONE table swaps the ROW as well as the column set —
               same props, same testids, same `expandedContent`. */
            const TableRow = isMobile ? OrderMobileTableRow : OrderTableRow;
            return (
              <TableRow
                key={order.id}
                order={order}
                isExpanded={isExpanded}
                isLoadingDetail={isLoadingDetail}
                onToggleExpand={toggleExpand}
                detail={detail}
                expandedContent={expandedContent}
                /* 🟡 NO `actionable` PROP, MATCHING `Approvals.js` AND NOT AN
                   OVERSIGHT. The green chevron means the server's role-dependent
                   `can_action` — "this is yours to act on in the approval
                   chain". **"Actionable" on THIS page is a different predicate
                   entirely: `status === 'rejected'`, i.e. your own order bounced
                   back and needs resubmitting.** CLAUDE.md is explicit that the
                   three meanings must NOT be unified, and passing this page's
                   predicate into a signal that means the other thing is exactly
                   that unification. If a "needs resubmitting" row marker is ever
                   wanted here it is a product decision and needs its own
                   constant in `constants/rowStyles.js`, not this prop. */
                rowTestId={`order-row-${order.id}`}
                toggleTestId={`order-toggle-${order.id}`}
                detailsTestId={`order-details-${order.id}`}
                testIdPrefix="my"
              />
            );
          })}
        </OrderTableShell>
      )}
      </div>

      {/* Pagination - Bottom */}
      {filteredOrders.length > 0 && (
        <Pagination
          totalItems={filteredOrders.length}
          currentPage={page}
          pageSize={effectivePageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* Order Completion — the ALWAYS-SHOWN confirm dialog, built by
          `hooks/useMyOrderExpansion.js` and rendered here, at PAGE level,
          exactly where it always stood. */}
      {expansionDialogs}

    </div>
  );
}

export default MyOrders;
